import { useState, type CSSProperties, type ReactElement } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, CheckCheck, Clock, Plus, Send, X as XIcon } from "lucide-react";
import { color, radius, space, useListItemMotion } from "@tokens/index";
import styles from "./Threads.module.css";
import { Button, ContentSwap, SkeletonRows, Text, TextInput } from "../../primitives";
import { staleDataStyle } from "../staleData";
import { classifyTaskStatus, taskStatusLabel, type TaskStatus } from "../taskStatus";
import type { WorkItem, WorkflowSummary } from "../../../experience-shell/workflows";
import { formatRelativeTime, statusDotColor } from "./formatters";
import { ActionButton, StatusDot } from "./shared";
import { hairline } from "./expandMotion";
import type { SelectedThreadInfo } from "./types";

function ThreadRow({
  thread,
  selected,
  status,
  queueName,
  preview,
  onSelect,
  index,
}: {
  readonly thread: WorkflowSummary;
  readonly selected: boolean;
  readonly status: TaskStatus | null;
  readonly queueName: string | null;
  readonly preview: string | null;
  readonly onSelect: () => void;
  readonly index: number;
}): ReactElement {
  const arrival = useListItemMotion(index, false);
  const hasSub = status !== null || queueName !== null;
  return (
    <motion.div {...arrival} style={{ display: "flex", minWidth: 0 }}>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={styles.row}
        data-selected={selected || undefined}
        style={{
          appearance: "none",
          textAlign: "left",
          position: "relative",
          flex: 1,
          border: "none",
          borderRadius: radius.md,
          padding: `${space[2]} ${space[3]}`,
          paddingLeft: space[4],
          minHeight: 40,
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          color: color.fg.default,
        }}
      >
        {selected && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              left: 4,
              top: space[2],
              bottom: space[2],
              width: 3,
              borderRadius: 2,
              background: color.accent.primary,
            }}
          />
        )}
        <Text role="body" size="sm" weight="semibold" truncate style={{ display: "block" }}>
          {thread.title}
        </Text>
        {hasSub && (
          <span
            style={{
              display: "flex",
              gap: space[2],
              alignItems: "center",
              marginTop: 2,
              minWidth: 0,
            }}
          >
            <StatusDot tone={status !== null ? statusDotColor(status) : color.fg.subtle} />
            <Text role="caption" size="xs" tone="muted" truncate>
              {[queueName, status !== null ? taskStatusLabel(status) : null]
                .filter(Boolean)
                .join(" · ")}
            </Text>
          </span>
        )}
        {preview !== null && (
          <Text
            role="caption"
            size="xs"
            tone="subtle"
            truncate
            style={{ display: "block", marginTop: space[1] }}
          >
            {preview}
          </Text>
        )}
      </button>
    </motion.div>
  );
}

export function ThreadList({
  threads,
  isLoading,
  isStale,
  selectedThreadId,
  onSelectThread,
  selectedThreadInfo,
  onStartNew,
  bordered,
}: {
  readonly threads: readonly WorkflowSummary[];
  /** No thread list on hand yet. Renders the skeleton. */
  readonly isLoading: boolean;
  /** `threads` belong to the previous task while the next list loads. Dims the rows. */
  readonly isStale: boolean;
  readonly selectedThreadId: string | null;
  readonly onSelectThread: (id: string) => void;
  readonly selectedThreadInfo: SelectedThreadInfo | null;
  readonly onStartNew: () => void;
  /** Right hairline when the list sits beside the pane. */
  readonly bordered: boolean;
}): ReactElement {
  return (
    <div
      data-testid="workflow-board-thread-list"
      style={{
        borderRight: bordered ? hairline : "none",
        overflowY: "auto",
        padding: `${space[3]} ${space[2]}`,
        display: "flex",
        flexDirection: "column",
        gap: space[1],
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <Text
        role="caps"
        size="xs"
        tone="muted"
        style={{ display: "block", padding: `${space[1]} ${space[2]} ${space[2]}` }}
      >
        Threads
      </Text>
      <ContentSwap ready={!isLoading} skeleton={<SkeletonRows label="Loading threads" rows={3} />}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: space[1],
            minWidth: 0,
            ...staleDataStyle(isStale),
          }}
        >
          <AnimatePresence initial={false}>
            {threads.map((thread, index) => {
              const isSelected = thread.id === selectedThreadId;
              return (
                <ThreadRow
                  key={thread.id}
                  index={index}
                  thread={thread}
                  selected={isSelected}
                  status={isSelected ? (selectedThreadInfo?.status ?? null) : null}
                  queueName={isSelected ? (selectedThreadInfo?.queueName ?? null) : null}
                  preview={isSelected ? (selectedThreadInfo?.preview ?? null) : null}
                  onSelect={() => onSelectThread(thread.id)}
                />
              );
            })}
          </AnimatePresence>
        </div>
      </ContentSwap>
      <Button
        variant="ghost"
        size="md"
        onClick={onStartNew}
        style={{ marginTop: space[1], justifyContent: "flex-start" }}
      >
        <Plus size={14} strokeWidth={2.5} aria-hidden="true" />
        New thread…
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Thread answer status bar
// ---------------------------------------------------------------------------

/**
 * Approval state of the active thread's seed task. Hidden when the seed
 * has no approval contract or the answer is not yet given.
 */
export function ThreadAnswerStatus({
  seed,
  now,
  canApprove,
  isApproving,
  isRejecting,
  onApprove,
  onReject,
}: {
  readonly seed: WorkItem;
  readonly now: Date;
  readonly canApprove: boolean;
  readonly isApproving: boolean;
  readonly isRejecting: boolean;
  readonly onApprove: ((id: string) => void) | undefined;
  readonly onReject: ((id: string, comment: string) => void) | undefined;
}): ReactElement | null {
  const status = classifyTaskStatus(seed, now);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  const isPendingApproval = status === "pending-approval";
  const isApproved = status === "completed" && seed.approvedAt !== null;
  if (!isPendingApproval && !isApproved) return null;

  const barStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: space[2],
    padding: `${space[2]} ${space[4]}`,
    borderBottom: hairline,
    background: isApproved ? color.status.successBg : color.status.warningBg,
    flexWrap: "wrap",
    flexShrink: 0,
    minWidth: 0,
  };

  if (isApproved) {
    const approverName = seed.approvedByDisplayName ?? seed.approvedBy ?? "approver";
    const approvedAt = seed.approvedAt;
    return (
      <div style={barStyle}>
        <Check size={12} strokeWidth={2.5} color={color.status.successFg} aria-hidden="true" />
        <Text role="caption" weight="medium" truncate style={{ color: color.status.successFg }}>
          Answer approved by {approverName}
          {approvedAt !== null ? ` · ${formatRelativeTime(approvedAt)}` : ""}
        </Text>
      </div>
    );
  }

  const busy = isApproving || isRejecting;

  const confirmRejection = (): void => {
    if (onReject === undefined || busy) return;
    const comment = reason.trim();
    if (comment.length === 0) return;
    onReject(seed.id, comment);
    setReason("");
    setRejecting(false);
  };

  if (canApprove && onApprove !== undefined) {
    return (
      <div style={barStyle}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: space[2],
            minWidth: 0,
            flex: 1,
          }}
        >
          <Clock size={12} strokeWidth={2.25} color={color.status.warningFg} aria-hidden="true" />
          <Text role="caption" weight="medium" truncate style={{ color: color.status.warningFg }}>
            Answer awaiting your approval
          </Text>
        </div>
        <div
          style={{ display: "inline-flex", gap: space[2], alignItems: "center", flexWrap: "wrap" }}
        >
          <ActionButton
            label="Approve"
            icon={<Check size={14} strokeWidth={2.5} aria-hidden="true" />}
            tone="successOutline"
            size="sm"
            disabled={busy}
            busy={isApproving}
            onClick={() => onApprove(seed.id)}
          />
          {onReject !== undefined && (
            <ActionButton
              label="Reject"
              icon={<XIcon size={14} strokeWidth={2.5} aria-hidden="true" />}
              tone="dangerOutline"
              size="sm"
              disabled={busy}
              onClick={() => setRejecting((v) => !v)}
            />
          )}
        </div>
        {rejecting && onReject !== undefined && (
          <div
            style={{
              display: "flex",
              gap: space[2],
              alignItems: "center",
              width: "100%",
              flexWrap: "wrap",
            }}
          >
            <TextInput
              autoFocus
              placeholder="Reason for rejection…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  confirmRejection();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setReason("");
                  setRejecting(false);
                }
              }}
              aria-label="Rejection reason"
              style={{ flex: 1, minWidth: 160 }}
            />
            <ActionButton
              label="Confirm rejection"
              icon={<Send size={14} strokeWidth={2.5} aria-hidden="true" />}
              tone="danger"
              size="sm"
              disabled={busy || reason.trim().length === 0}
              busy={isRejecting}
              onClick={confirmRejection}
            />
          </div>
        )}
      </div>
    );
  }

  const queueName = seed.requestedByQueueName ?? null;
  return (
    <div style={barStyle}>
      <Clock size={12} strokeWidth={2.25} color={color.status.warningFg} aria-hidden="true" />
      <Text role="caption" weight="medium" truncate style={{ color: color.status.warningFg }}>
        {queueName !== null && queueName.length > 0
          ? `Answer awaiting approval from ${queueName}`
          : "Answer awaiting approval"}
      </Text>
    </div>
  );
}

/**
 * Bar that settles an open conversation. Shows a static "closed" label
 * when no open message remains.
 */
export function ConversationCloseBar({
  onClose,
  isClosing,
  isClosed,
  openLabel,
  closedLabel,
}: {
  readonly onClose: () => void;
  readonly isClosing: boolean;
  readonly isClosed: boolean;
  readonly openLabel: string;
  readonly closedLabel: string;
}): ReactElement {
  const barStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: space[2],
    padding: `${space[2]} ${space[4]}`,
    borderBottom: hairline,
    flexShrink: 0,
    minWidth: 0,
  };

  if (isClosed) {
    return (
      <div style={{ ...barStyle, background: color.status.successBg }}>
        <CheckCheck size={13} strokeWidth={2.5} color={color.status.successFg} aria-hidden="true" />
        <Text role="caption" weight="medium" truncate style={{ color: color.status.successFg }}>
          {closedLabel}
        </Text>
      </div>
    );
  }

  return (
    <div style={barStyle}>
      <ActionButton
        label={isClosing ? "Closing…" : openLabel}
        icon={<CheckCheck size={14} strokeWidth={2.5} aria-hidden="true" />}
        tone="ghost"
        size="sm"
        disabled={isClosing}
        busy={isClosing}
        onClick={onClose}
      />
    </div>
  );
}
